import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { DollarSign, TrendingUp, FileText, PiggyBank, Sparkles, ArrowUpRight, ArrowDownRight, Receipt } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    totalDeductions: 0,
    estimatedTax: 0,
  });
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setDataLoading(true);
      const [incomeRes, expensesRes, deductionsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/income`),
        axios.get(`${API}/expenses`),
        axios.get(`${API}/deductions`),
        axios.get(`${API}/tax-settings`),
      ]);

      const totalIncome = incomeRes.data.reduce((sum, item) => sum + item.amount, 0);
      const totalExpenses = expensesRes.data.reduce((sum, item) => sum + item.amount, 0);
      const totalDeductions = deductionsRes.data.total_claimed || 0;

      // Calculate estimated tax
      if (totalIncome > 0) {
        const taxCalcRes = await axios.post(`${API}/tax-calculation`, {
          total_income: totalIncome,
          total_deductions: totalDeductions,
          filing_status: settingsRes.data.filing_status,
          state: settingsRes.data.state,
        });
        setStats({
          totalIncome,
          totalExpenses,
          totalDeductions,
          estimatedTax: taxCalcRes.data.total_tax,
        });
      } else {
        setStats({ totalIncome, totalExpenses, totalDeductions, estimatedTax: 0 });
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setDataLoading(false);
    }
  };

  const askAI = async () => {
    if (!aiQuestion.trim()) {
      toast.error("Please enter a question");
      return;
    }

    setLoading(true);
    setAiResponse("");
    try {
      const response = await axios.post(`${API}/ai-advice`, {
        question: aiQuestion,
        context: stats,
      });
      setAiResponse(response.data.advice);
    } catch (error) {
      console.error("Error getting AI advice:", error);
      toast.error("Failed to get AI advice");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Income",
      value: `$${stats.totalIncome.toLocaleString()}`,
      icon: DollarSign,
      color: "text-green-600",
      bgColor: "bg-green-50",
      trend: "+12%",
      trendUp: true,
    },
    {
      title: "Total Expenses",
      value: `$${stats.totalExpenses.toLocaleString()}`,
      icon: ArrowDownRight,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      trend: "-3%",
      trendUp: false,
    },
    {
      title: "Deductions Found",
      value: `$${stats.totalDeductions.toLocaleString()}`,
      icon: PiggyBank,
      color: "text-primary",
      bgColor: "bg-primary/10",
      trend: "+8%",
      trendUp: true,
    },
    {
      title: "Estimated Tax",
      value: `$${stats.estimatedTax.toLocaleString()}`,
      icon: FileText,
      color: "text-accent",
      bgColor: "bg-accent/10",
      trend: "Q1 2024",
      trendUp: null,
    },
  ];

  const chartData = [
    { name: "Income", value: stats.totalIncome },
    { name: "Expenses", value: stats.totalExpenses },
    { name: "Deductions", value: stats.totalDeductions },
    { name: "Est. Tax", value: stats.estimatedTax },
  ];

  const COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-4"
      >
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-light tracking-tight" data-testid="dashboard-title">
          Your Tax Overview
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
          Track your income, expenses, and maximize your deductions with AI-powered insights.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card className="hover:shadow-md transition-all" data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-3 flex-1">
                      <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                      <p className="text-3xl font-mono font-semibold tracking-tight">{stat.value}</p>
                      {stat.trendUp !== null && (
                        <div className={`flex items-center text-sm ${stat.trendUp ? 'text-green-600' : 'text-muted-foreground'}`}>
                          {stat.trendUp ? <TrendingUp className="w-4 h-4 mr-1" /> : null}
                          <span>{stat.trend}</span>
                        </div>
                      )}
                      {stat.trendUp === null && <p className="text-sm text-muted-foreground">{stat.trend}</p>}
                    </div>
                    <div className={`${stat.bgColor} ${stat.color} p-3 rounded-xl`}>
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Charts and AI Advisor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading font-light">Financial Overview</CardTitle>
            <CardDescription>Year-to-date summary</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* AI Tax Advisor */}
        <Card className="border-accent/20">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-accent" />
              <CardTitle className="font-heading font-light">AI Tax Advisor</CardTitle>
            </div>
            <CardDescription>Get instant tax advice powered by GPT-5.2</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="Ask me anything about taxes... e.g., 'What deductions am I missing?'"
              value={aiQuestion}
              onChange={(e) => setAiQuestion(e.target.value)}
              rows={3}
              className="resize-none"
              data-testid="ai-question-input"
            />
            <Button
              onClick={askAI}
              disabled={loading}
              className="w-full rounded-full"
              data-testid="ask-ai-button"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Thinking...
                </span>
              ) : (
                <span className="flex items-center">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Ask AI
                </span>
              )}
            </Button>
            {aiResponse && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-secondary/50 rounded-lg border border-border"
                data-testid="ai-response"
              >
                <p className="text-sm leading-relaxed">{aiResponse}</p>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading font-light">Quick Actions</CardTitle>
          <CardDescription>Common tasks to keep your taxes organized</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Button variant="outline" className="h-auto py-6 flex flex-col items-center space-y-2" onClick={() => window.location.href = "/expenses"} data-testid="quick-action-add-expense">
              <Receipt className="w-6 h-6" />
              <span>Add Expense</span>
            </Button>
            <Button variant="outline" className="h-auto py-6 flex flex-col items-center space-y-2" onClick={() => window.location.href = "/income"} data-testid="quick-action-record-income">
              <DollarSign className="w-6 h-6" />
              <span>Record Income</span>
            </Button>
            <Button variant="outline" className="h-auto py-6 flex flex-col items-center space-y-2" onClick={() => window.location.href = "/calculator"} data-testid="quick-action-calculate-tax">
              <FileText className="w-6 h-6" />
              <span>Calculate Tax</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}